import { useEffect, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { FormattedMessage } from 'react-intl'
import { TestIds } from '@/constants/testIds'
import { useAtomValue, useSetAtom } from 'jotai'
import {
  fetchVerificationInfoAtom,
  verificationInfoAtom,
  activeVerificationInfo,
  activeVerificationWorkOrderIndexAtom,
} from '@/state/verification'
import {
  initialWorkOrderIdAtom,
  emailLinkWorkOrderIdAtom,
} from '@/state/authentication/login'
import { VerifyInfoForm } from '@/components/verification/VerifyInfoForm'
import { VerifyInfoHeader } from '@/components/verification/form-sections/VerifyInfoHeader'
import { VerifyInfoFormProvider } from '@/components/verification/VerifyInfoFormContext'

export const Route = createFileRoute('/verifyInfo')({
  component: VerifyInfo,
})

function VerifyInfo() {
  useDocumentTitle('meta.title.verifyInfo')
  const navigate = useNavigate()
  const [loaded, setLoaded] = useState(false)
  const verificationInfo = useAtomValue(verificationInfoAtom)
  const activeVerificationWorkOrder = useAtomValue(activeVerificationInfo)
  const initialWorkOrderId = useAtomValue(initialWorkOrderIdAtom)
  const emailLinkWorkOrderId = useAtomValue(emailLinkWorkOrderIdAtom)
  const setActiveIndex = useSetAtom(activeVerificationWorkOrderIndexAtom)

  const getVerificationInfo = useSetAtom(fetchVerificationInfoAtom)

  useEffect(() => {
    const fetchVerificationInfo = async () => {
      await getVerificationInfo()
      setLoaded(true)
    }
    fetchVerificationInfo()
  }, [getVerificationInfo])

  // Point to the WO that the email link targeted, not always index 0
  useEffect(() => {
    if (!loaded) return
    const workOrders = verificationInfo.data?.data?.multipleWorkorders
    if (!workOrders?.length) return
    // emailLinkWorkOrderId is the most authoritative source; fall back to initialWorkOrderId
    const targetWO =
      emailLinkWorkOrderId != null && emailLinkWorkOrderId > 0
        ? emailLinkWorkOrderId
        : initialWorkOrderId
    if (targetWO) {
      const idx = workOrders.findIndex((wo) => wo.workOrderId === targetWO)
      if (idx >= 0) {
        setActiveIndex(idx)
        return
      }
    }
    setActiveIndex(0)
  }, [
    loaded,
    verificationInfo.data,
    emailLinkWorkOrderId,
    initialWorkOrderId,
    setActiveIndex,
  ])

  useEffect(() => {
    if (activeVerificationWorkOrder === null && loaded) {
      navigate({ to: '/dashboard' })
    }
  }, [activeVerificationWorkOrder, loaded, navigate])

  useEffect(() => {
    if (!loaded) return
    if (verificationInfo.data?.data?.multipleWorkorders?.length === 0) {
      navigate({ to: '/dashboard' })
    }
  }, [loaded, verificationInfo.data?.data?.multipleWorkorders, navigate])

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-4 mt-16 mb-24">
      <h2
        data-testid={TestIds.verifyInfo.pageTitle}
        className="text-2xl font-bold text-center"
      >
        <FormattedMessage id="verifyInfo.title" />
      </h2>
      <VerifyInfoFormProvider>
        <VerifyInfoHeader />
        <VerifyInfoForm />
      </VerifyInfoFormProvider>
    </div>
  )
}
